// Frontier-focus: filter the full discovery map to the learner's horizon — every
// UNLOCKED node (its requirements all satisfied: mastered or playable now) plus the
// entry sources, and every locked node within `hops` forward edges of those (the next
// 1–2 steps). Node positions are kept, so it renders as a zoomed region of the real map.
// Tames breadth (F13): the visible set stays small no matter how big the lattice grows,
// and answers "where do I start" (the entry is always shown). Pure — fed to the same
// DiscoveryMap via its layout override, like the path view.
import type { MapLayout } from "../songs/map-layout";

/** `satisfied(atom)` = the learner has (or transparently meets) that requirement —
 *  pass `buildMastery(...).satisfied`. */
export function frontierFocus(
  layout: MapLayout,
  satisfied: (atom: string) => boolean,
  hops: number,
): MapLayout {
  const hasIncoming = new Set(layout.edges.map((e) => e.to));
  // Base = unlocked (all requirements met) ∪ entry sources (no incoming edge, so a fresh
  // learner with nothing mastered still sees where to begin).
  const include = new Set(
    layout.nodes
      .filter((n) => !hasIncoming.has(n.id) || n.requirements.every(satisfied))
      .map((n) => n.id),
  );
  const adj = new Map<string, string[]>();
  for (const e of layout.edges) {
    const list = adj.get(e.from);
    if (list) list.push(e.to);
    else adj.set(e.from, [e.to]);
  }
  // Reveal up to `hops` forward layers of otherwise-locked nodes.
  let frontier = [...include];
  for (let h = 0; h < hops; h++) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const to of adj.get(id) ?? []) {
        if (!include.has(to)) {
          include.add(to);
          next.push(to);
        }
      }
    }
    frontier = next;
  }
  return {
    nodes: layout.nodes.filter((n) => include.has(n.id)),
    edges: layout.edges.filter((e) => include.has(e.from) && include.has(e.to)),
  };
}
